import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCalendarModule } from 'ng-zorro-antd/calendar';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzFormModule } from 'ng-zorro-antd/form';
import { AppointmentsTableComponent } from '../../components/appointments-table/appointments-table.component';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, RouterLink } from '@angular/router';
import { CardListComponent } from '../../components/card-list/card-list.component';
import { DataService } from '../../services/data.service';
import { it_IT, NZ_I18N } from 'ng-zorro-antd/i18n';
import { HttpService } from '../../services/http.service';
import { forkJoin, startWith } from 'rxjs';
import { subYears, addYears, subDays } from 'date-fns';
import { CarouselCardComponent } from '../../components/utility/carousel-card/carousel-card.component';
import { ICarouselElement } from '../../interfaces/carousel-element.interface';
import { CardNextAppointmentComponent } from '../../components/card-next-appointment/card-next-appointment.component';
import { UserAccountDTO } from '../../classes/user-account.dto';
import { StorageService } from '../../services/storage.service';
import { CalendarKeyboardNavigationDirective } from '../../directives/calendar-keyboard-navigation.directive';

@Component({
    selector: 'app-medical-consultations',
    imports: [
        CommonModule,
        FormsModule,
        NzTagModule,
        NzCollapseModule,
        NzModalModule,
        NzCardModule,
        NzIconModule,
        NzListModule,
        NzCalendarModule,
        NzBadgeModule,
        NzGridModule,
        NzFormModule,
        AppointmentsTableComponent,
        ReactiveFormsModule,
        FormsModule,
        NzDatePickerModule,
        NzTimePickerModule,
        TranslateModule,
        RouterLink,
        CardListComponent,
        CarouselCardComponent,
        CardNextAppointmentComponent,
        CalendarKeyboardNavigationDirective,
    ],
    providers: [{ provide: NZ_I18N, useValue: it_IT }],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './medical-consultations.component.html',
    styleUrl: './medical-consultations.component.scss'
})
export class MedicalConsultationsComponent implements OnInit, OnDestroy {
  tasks = [
    { text: 'Misura la pressione', completed: true },
    { text: 'Misura la glicemia', actionText: 'Registra', completed: false },
    { text: 'Misura il peso', actionText: 'Registra', completed: false },
    { text: 'Prendi il farmaco', actionText: 'Dettagli', completed: false },
  ];
  columns: any;
  appointmentsText: any;
  pastAppointmentsText: any;
  appointments: any = [];
  pastAppointments: any = [];
  form: FormGroup<any>;
  healthBookingsText: any;
  hasOurServices: boolean = false;
  carouselElements?: ICarouselElement;
  isDesktop: boolean = true;
  functions: string[] = [];


  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
    private dataService: DataService,
    private http: HttpService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private storage: StorageService
  ) {
    this.form = this.fb.group({
      date: new FormControl(null, Validators.required),
      time: new FormControl(null, Validators.required),
    });
    this.translateService.onLangChange.subscribe((_: LangChangeEvent) => {
      this.getTexts();
      this.createCarouselElements();
    });
    this.getTexts();
  }

  ngOnInit() {
    this.getAppointments();
    this.createCarouselElements();
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize.bind(this));
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    this.functions = userData.functions;
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
  }

  public getAppointments(): void {
    const individualId = JSON.parse(localStorage.getItem('userData')!).individualId;
    const language = this.translateService.currentLang;
    const $futureAppointments = this.http.getUserStoryActual(individualId, 6, language);
    const $pastAppointments = this.http.getUserStoryPassed(individualId, 6, language);
    forkJoin([$futureAppointments, $pastAppointments]).subscribe(([resFuture, resPast]) => {
      if (resFuture.lista.length > 0) {
        resFuture.lista.map((element: any) => {
          const [date, time] = element.consultDate.split(' ');
          element['time'] = time;
          element.consultDate = date;
          element.statusText = 'statusTable.' + element?.status?.toLowerCase();
        });
        this.appointments = resFuture.lista;
        this.dataService.setAppointments(this.appointments);
      }
      if (resPast.lista.length > 0) {
        resPast.lista.map((element: any) => {
          const [date, time] = element.consultDate.split(' ');
          element['time'] = time;
          element.consultDate = date;
          element.statusText = 'statusTable.' + element?.status?.toLowerCase();
        });
        this.pastAppointments = resPast.lista;
        this.dataService.setAppointments(this.pastAppointments);
      }
      this.cdr.detectChanges();
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private getTexts() {
    this.translateService.get('medicalConsultations.appointmentsText').subscribe((appointmentsText) => {
      this.appointmentsText = appointmentsText;
    });
    this.translateService.get('medicalConsultations.pastAppointmentsText').subscribe((appointmentsText) => {
      this.pastAppointmentsText = appointmentsText;
    });
     this.translateService.get('ourServices').subscribe((ourServices: any) => {
      if (ourServices && ourServices.servicesList && ourServices.servicesList.length > 0) {
        this.healthBookingsText = null;
        this.healthBookingsText = ourServices.servicesList;
        this.hasOurServices = true;
      } else {
        this.hasOurServices = false;
      }
    });
    this.translateService.get('appointmentsTable').subscribe((texts) => {
      this.columns = texts.columns;
    });
  }

  onValueChange(value: Date): void {
    console.log(`Current value: ${value}`);
  }

  onPanelChange(change: { date: Date; mode: string }): void {
    console.log(`Current value: ${change.date}`);
    console.log(`Current mode: ${change.mode}`);
  }

  private getRows() {
    this.translateService.get('steps.tipsideWithModal.modalContent.texts').subscribe((res) => {});
  }

  private createCarouselElements(): void {
    const userData = localStorage.getItem('userData');

    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        this.functions = parsedUserData.functions || [];
      } catch (error) {
        console.error('Errore nel parsing di userData:', error);
      }
    }

    this.translateService.get('medicalConsultations.carouselContent').subscribe((carouselContent) => {
      this.carouselElements = {
        carouselText: {
          title: carouselContent.title,
          subtitle: carouselContent.subtitle,
          middleNumberText: carouselContent.of,
        },
        cardsContent: [],
      };

      if (this.functions.includes('PPP')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathPsiTelevisitFU',
          image: 'woman-yoga.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[0],
        });
      }

      if (this.functions.includes('PNP')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathNutTelevisitFU',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[1],
        });
      }

      if (this.functions.includes('PNF')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathNutTelevisitFU',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[2],
        });
      }

      if (this.functions.includes('PPF')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathPsiTelevisitFU',
          image: 'woman-yoga.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[3],
        });
      }

      if (this.functions.includes('TSP')) {
        this.carouselElements.cardsContent.push({
          id: 'SpecialistTelevisitVideoPayment',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[4],
        });
      }

      if (this.functions.includes('TGP')) {
        this.carouselElements.cardsContent.push({
          id: 'GenericTelevisitVideoPayment',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[5],
        });
      }
    });
  }

  protected onCarouselCarePathwayEmit(idCarePathway: string): void {
    this.router.navigate(['/medical-consultations/showcase-discover-care-pathway'], { queryParams: { id: idCarePathway } });
  }

  isHighlighted(date: Date): any {
    const mergedArray = [...this.appointments, ...this.pastAppointments];
    return mergedArray.some((d) => new Date(d.appointmentStartDate).toDateString() === date.toDateString());
  }

  checkScreenSize() {
    this.isDesktop = window.innerWidth >= 768;
  }
}
